import { useState, useEffect } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Lightbulb, Sparkles, Wifi, WifiOff } from 'lucide-react';
import { SuggestionsList } from '@/components/guidance/SuggestionsList';
import { useRealtimeGuidance } from '@/hooks/useRealtimeGuidance';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';

interface MobileGuidancePanelProps {
  questions: string[];
  notes: Array<{ type: string; content: string }>;
  isOpen: boolean;
  onClose: () => void;
  noteSpaceId?: string;
}

export const MobileGuidancePanel = ({ questions, notes, isOpen, onClose, noteSpaceId }: MobileGuidancePanelProps) => {
  const isLiveGuidanceAvailable = useFeatureFlag('enableLiveGuidance');
  const isRealtimeEnabled = useFeatureFlag('enableRealtimeGuidance');

  const {
    suggestions,
    isLoading,
    isConnected,
    error,
    requestGuidance,
    updateContext,
    streamingContent
  } = useRealtimeGuidance(noteSpaceId);

  useEffect(() => {
    if (isOpen && questions?.length) {
      // Determine current stage based on notes count
      let currentStage: 'early' | 'middle' | 'late' = 'early';
      if (notes.length > 10) currentStage = 'late';
      else if (notes.length > 3) currentStage = 'middle';

      updateContext(questions, notes, currentStage);
      requestGuidance();
    }
  }, [questions, notes, isOpen, updateContext, requestGuidance]);

  // Don't render at all if feature flag is disabled
  if (!isLiveGuidanceAvailable) {
    return null;
  }

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="pb-4">
          <DrawerTitle className="text-center flex items-center justify-center gap-2">
            Live Guidance
            {isRealtimeEnabled && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {isConnected ? (
                  <><Wifi className="h-3 w-3 text-green-500" /> Realtime</>
                ) : (
                  <><WifiOff className="h-3 w-3 text-red-500" /> Offline</>
                )}
              </div>
            )}
          </DrawerTitle>
          <DrawerDescription className="text-center">
            AI-powered suggestions based on your interview progress
          </DrawerDescription>
        </DrawerHeader>
        <ScrollArea className="flex-1">
          <div className="px-6 pb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900 flex items-center space-x-2">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                <span>Suggestions</span>
                <Badge variant="secondary" className="text-xs">{suggestions.length}</Badge>
              </h3>
              {isLoading && (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs text-gray-500">
                    {isRealtimeEnabled ? 'Generating guidance...' : 'Thinking...'}
                  </span>
                </div>
              )}
            </div>
            
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
            
            {streamingContent && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md mb-4">
                <p className="text-xs text-blue-600 font-mono whitespace-pre-wrap">
                  {streamingContent}
                </p>
              </div>
            )}
            
            {suggestions.length > 0 ? (
              <SuggestionsList suggestions={suggestions} />
            ) : !isLoading ? (
              <div className="text-center py-8 text-gray-500">
                <Sparkles className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Start adding notes to get AI-powered guidance!</p>
              </div>
            ) : null}
          </div>
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  );
};